import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { getContent, saveContent, deleteContent } from '../../lib/api';
import type { ContentBundle } from '../../lib/types';
import { useT } from '../../i18n/LangProvider';

type Tab = 'sessions' | 'speakers';

export default function AdminDashboard() {
  const { t } = useT();
  const [tab, setTab] = useState<Tab>('sessions');
  const [content, setContent] = useState<ContentBundle | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [editing, setEditing] = useState<any | null>(null);
  const [busy, setBusy] = useState(false);

  const load = () => getContent(true).then(setContent).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing) return;
    setBusy(true);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    try { await saveContent(tab as any, editing); await load(); setEditing(null); }
    catch (e) { alert((e as Error).message); }
    finally { setBusy(false); }
  };
  const del = async (id: string) => {
    if (!confirm('Delete this item?')) return;
    setBusy(true);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    try { await deleteContent(tab as any, id); await load(); }
    catch (e) { alert((e as Error).message); }
    finally { setBusy(false); }
  };
;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const list: any[] = content ? (content as any)[tab] ?? [] : [];

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-20 pt-36 pb-32 grid grid-cols-12 gap-6">
      <div className="col-span-12 md:col-span-10 md:col-start-2">
        <div className="flex items-baseline justify-between flex-wrap gap-4 mb-10">
          <div>
            <p className="text-xs tracking-[0.25em] uppercase text-brand-red mb-5">{t('admin.kicker')}</p>
            <h1 className="font-display text-5xl md:text-6xl tracking-tight">{t('admin.title')}</h1>
          </div>
          <div className="flex items-center gap-5 flex-wrap">
            <Link to="/admin/register" className="text-sm text-brand-red hover:underline lowercase">{t('admin.manageRegistration')} →</Link>
            <Link to="/admin/teams" className="text-sm text-brand-red hover:underline lowercase">manage teams link →</Link>
            <Link to="/admin/poll" className="text-sm text-brand-red hover:underline lowercase">{t('admin.managePoll')} →</Link>
            <Link to="/admin/editors" className="text-sm text-brand-red hover:underline lowercase">{t('admin.manageAdmins')} →</Link>
          </div>
        </div>

        <div className="flex gap-2 flex-wrap border-b border-white/[0.07] pb-4 mb-8">
          {(['sessions', 'speakers'] as Tab[]).map((tk) => (
            <button key={tk} onClick={() => { setTab(tk); setEditing(null); }}
              className={`px-4 py-2 rounded-full text-sm capitalize transition-colors ${tab === tk ? 'bg-brand-red text-white' : 'bg-bg-elev text-ink-muted hover:text-ink border border-white/[0.08]'}`}
            >{t(`admin.tabs.${tk}`)}</button>
          ))}
        </div>

        
          <section>
            <div className="flex justify-end mb-4">
              <button onClick={() => setEditing(blankFor(tab))} className="bg-brand-red text-white text-sm rounded-full px-5 py-2 hover:bg-brand-red-soft">+ New {tab.slice(0, -1)}</button>
            </div>
            <div className="space-y-2">
              {list.map((item) => (
                <div key={item.id} className="flex items-center justify-between gap-3 p-4 bg-bg-elev rounded-xl border border-white/[0.07]">
                  <div className="min-w-0">
                    <div className="text-white font-medium flex items-center gap-2">
                      {item.TitleEn || item.Title}
                      {!item.Published && <span className="text-[10px] text-amber-400 border border-amber-400/40 rounded px-1.5 py-0.5">draft</span>}
                    </div>
                    <div className="text-ink-muted text-xs mt-0.5 truncate">
                      {tab === 'sessions' && `${item.StartTime ? new Date(item.StartTime).toLocaleString() : ''} · ${item.Room || ''}`}
                      {tab === 'speakers' && `${item.JobTitleEn || item.JobTitle || ''}${item.Company ? ' · ' + item.Company : ''}`}
                    </div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button onClick={() => setEditing({ ...item })} className="text-xs px-3 py-1.5 rounded-full border border-white/[0.15] text-ink hover:border-white/40">Edit</button>
                    <button onClick={() => del(item.id)} className="text-xs px-3 py-1.5 rounded-full border border-white/[0.15] text-ink hover:border-brand-red hover:text-brand-red">Delete</button>
                  </div>
                </div>
              ))}
              {list.length === 0 && <p className="text-ink-muted">Nothing here yet.</p>}
            </div>
          </section>
      </div>

      <AnimatePresence>
        {editing && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[100] flex items-start justify-center overflow-y-auto p-6 pt-16"
            onClick={(e) => { if (e.target === e.currentTarget) setEditing(null); }}>
            <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 30, opacity: 0 }} className="bg-bg-elev rounded-2xl w-full max-w-xl p-8 border border-white/[0.08]">
              <h2 className="font-display text-2xl mb-6 tracking-tight text-ink">{editing.id ? 'Edit' : 'New'} {tab.slice(0, -1)}</h2>
              <EditorFields tab={tab} item={editing} onChange={setEditing} />
              <div className="flex gap-3 mt-6">
                <button onClick={save} disabled={busy} className="bg-brand-red text-white rounded-full px-5 py-2 text-sm disabled:opacity-40 hover:bg-brand-red-soft">{busy ? 'Saving…' : 'Save'}</button>
                <button onClick={() => setEditing(null)} className="rounded-full px-5 py-2 text-sm border border-white/[0.15] text-ink hover:border-white/40">Cancel</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function blankFor(tab: Tab): any {
  if (tab === 'sessions') return { TitleEn: '', TitleFr: '', DescriptionEn: '', DescriptionFr: '', StartTime: '', EndTime: '', Room: '', SpeakerIds: '', Capacity: '', Tags: '', JoinUrl: '', Published: false };
  if (tab === 'speakers') return { TitleEn: '', TitleFr: '', JobTitleEn: '', JobTitleFr: '', Company: '', BioEn: '', BioFr: '', PhotoUrl: '', LinkedInUrl: '', Order: '', RowBreak: false, Published: false };
  return { TitleEn: '', TitleFr: '', DescriptionEn: '', DescriptionFr: '', Published: false };
}

const FIELD = 'w-full rounded-xl border border-white/[0.12] bg-bg-base p-2.5 text-sm text-ink focus:border-brand-red outline-none mt-1';
const LABEL = 'text-xs text-ink-muted uppercase tracking-wider';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function EditorFields({ tab, item, onChange }: { tab: Tab; item: any; onChange: (v: any) => void }) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const set = (k: string, v: any) => onChange({ ...item, [k]: v });
  const text = (k: string, label: string, type = 'text') => (
    <label className="block"><span className={LABEL}>{label}</span><input className={FIELD} type={type} value={item[k] ?? ''} onChange={(e) => set(k, e.target.value)} /></label>
  );
  // bilingual text pair (EN + FR side by side)
  const bi = (base: string, label: string) => (
    <div className="grid grid-cols-2 gap-3">
      <label className="block"><span className={LABEL}>{label} (EN)</span><input className={FIELD} value={item[`${base}En`] ?? ''} onChange={(e) => set(`${base}En`, e.target.value)} /></label>
      <label className="block"><span className={LABEL}>{label} (FR)</span><input className={FIELD} value={item[`${base}Fr`] ?? ''} onChange={(e) => set(`${base}Fr`, e.target.value)} /></label>
    </div>
  );
  const biArea = (base: string, label: string) => (
    <div className="grid grid-cols-2 gap-3">
      <label className="block"><span className={LABEL}>{label} (EN)</span><textarea className={`${FIELD} min-h-[80px]`} value={item[`${base}En`] ?? ''} onChange={(e) => set(`${base}En`, e.target.value)} /></label>
      <label className="block"><span className={LABEL}>{label} (FR)</span><textarea className={`${FIELD} min-h-[80px]`} value={item[`${base}Fr`] ?? ''} onChange={(e) => set(`${base}Fr`, e.target.value)} /></label>
    </div>
  );
  return (
    <div className="grid gap-4">
      {bi('Title', tab === 'speakers' ? 'Full name' : 'Title')}
      {tab === 'sessions' && <>
        {biArea('Description', 'Description')}
        <div className="grid grid-cols-2 gap-4">
          {text('StartTime', 'Start time', 'datetime-local')}
          {text('EndTime', 'End time', 'datetime-local')}
        </div>
        {text('JoinUrl', 'Join URL (Teams / Zoom / Meet)')}
        {text('SpeakerIds', 'Speaker IDs')}
      </>}
      {tab === 'speakers' && <>
        {bi('JobTitle', 'Job title')}
        {text('Company', 'Company')}
        {biArea('Bio', 'Bio')}
        {text('PhotoUrl', 'Photo URL')}
        {text('Order', 'Page order (1 = first, empty = last)', 'number')}
        <label className="flex items-center gap-2 mt-1">
          <input type="checkbox" checked={!!item.RowBreak} onChange={(e) => set('RowBreak', e.target.checked)} />
          <span className="text-sm text-ink">Force new row after this speaker (desktop layout only)</span>
        </label>
      </>}
      <label className="flex items-center gap-2 mt-2">
        <input type="checkbox" checked={!!item.Published} onChange={(e) => set('Published', e.target.checked)} />
        <span className="text-sm text-ink">Published</span>
      </label>
    </div>
  );
}
